import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { marketingUrl } from "../lib/marketing";

interface TopBarProps {
  onMenuClick: () => void;
}

export default function TopBar({ onMenuClick }: TopBarProps) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [menuOpen]);

  async function onLogout() {
    setMenuOpen(false);
    const lang = user?.lang ?? i18n.language;
    await logout();
    window.location.assign(marketingUrl(lang));
  }

  return (
    <header className="sticky top-0 z-20 h-16 border-b border-slate-200 bg-white flex items-center px-4 md:px-6 gap-3">
      <button
        type="button"
        onClick={onMenuClick}
        aria-label={t("appShell.openNav")}
        className="md:hidden p-2 -ml-2 rounded-md text-slate-700 hover:bg-slate-100"
      >
        <span aria-hidden="true" className="block w-5 h-0.5 bg-current" />
        <span aria-hidden="true" className="block w-5 h-0.5 bg-current mt-1" />
        <span aria-hidden="true" className="block w-5 h-0.5 bg-current mt-1" />
      </button>

      <Link to="/" className="font-bold text-xl tracking-tight text-slate-900">
        MetricSpot
      </Link>

      <div className="flex-1" />

      <div ref={menuRef} className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={t("appShell.accountMenu")}
          data-testid="account-menu"
          className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100"
        >
          <span className="hidden sm:inline">{user?.email}</span>
          <span className="w-7 h-7 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center font-semibold">
            {(user?.name || user?.email || "?").trim().slice(0, 1).toUpperCase()}
          </span>
        </button>
        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 top-full mt-1 w-48 rounded-md border border-slate-200 bg-white shadow-lg py-1 z-30"
          >
            <div className="px-3 py-2 border-b border-slate-100 text-xs text-slate-500 truncate">
              {user?.email}
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={onLogout}
              className="block w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
            >
              {t("appShell.logout")}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
